import React, { useCallback, useState } from 'react';
import CustomModal from '../../util_components/custom_modal';
import Button from '../../util_components/button';
import { offHours, offHoursConfirmCopy, offHoursTooltip } from './off_hours';
import type { ContactAction, OffHoursInfo } from './off_hours';
import type { CallWindow } from './local_time';

interface Pending {
  action: ContactAction;
  info: OffHoursInfo;
  run: () => void;
}

interface DialogProps {
  pending: Pending | null;
  on_cancel: () => void;
  on_confirm: () => void;
}

const BTN = { padding: '6px 14px', fontSize: 13, margin: 0 };

const DETAIL_ID = 'off-hours-detail';

// Cancel is the first button; focus it on open so Enter is the safe choice.
const focus_cancel = (el: HTMLDivElement | null) => {
  el?.querySelector('button')?.focus();
};

export function OffHoursConfirmDialog({ pending, on_cancel, on_confirm }: DialogProps) {
  const copy = pending ? offHoursConfirmCopy(pending.action) : null;
  return (
    <CustomModal
      show={!!pending}
      close={on_cancel}
      inner_style={{ width: 'min(440px, 96vw)', borderRadius: 12 }}
    >
      {pending && copy ? (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-label="Off hours"
          aria-describedby={DETAIL_ID}
          ref={focus_cancel}
          className="flex flex-col gap-3"
        >
          <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            {copy.question}
          </div>
          <div id={DETAIL_ID} className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {offHoursTooltip(pending.info)}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="secondary" onClick={on_cancel} custom_style={BTN}>
              Cancel
            </Button>
            <Button onClick={on_confirm} custom_style={BTN}>
              {copy.confirm_label}
            </Button>
          </div>
        </div>
      ) : null}
    </CustomModal>
  );
}

// guard(action, run): runs now inside calling hours (or with no usable zone),
// else asks first. Render `dialog` once in the page.
export function useOffHoursGate(
  zone: string | null | undefined,
  call_window: CallWindow | null | undefined
): { guard: (action: ContactAction, run: () => void) => void; dialog: React.ReactElement } {
  const [pending, set_pending] = useState<Pending | null>(null);
  const guard = useCallback(
    (action: ContactAction, run: () => void) => {
      const info = offHours(zone, Date.now(), call_window);
      if (!info) {
        run();
        return;
      }
      set_pending({ action, info, run });
    },
    [zone, call_window]
  );
  const on_cancel = useCallback(() => set_pending(null), []);
  const on_confirm = useCallback(() => {
    set_pending(null);
    pending?.run();
  }, [pending]);
  return {
    guard,
    dialog: (
      <OffHoursConfirmDialog pending={pending} on_cancel={on_cancel} on_confirm={on_confirm} />
    )
  };
}
